import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import PlatformStatusPicker from "../components/hh/PlatformStatusPicker";
import { useAlertContext } from "../context/AlertContext";
import {
    getCandidatesStatuses,
    getCandidateHhStatusMapping,
    getHhSyncMismatches,
    importCandidateStatusFromHh,
    applyCandidateHhStatus,
} from "../services/candidateApi";

function displayDate(value) {
    if (!value) return "Не проверялось";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("ru-RU");
}

function displayStatusDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

function MismatchCard({ item, statuses, mapping, onQueued, onImported }) {
    const { showAlert } = useAlertContext();
    const [selectedStatus, setSelectedStatus] = useState(item.platform_status);
    const [statusDate, setStatusDate] = useState(item.status_date || "");
    const [saving, setSaving] = useState(false);
    const [reverseStatus, setReverseStatus] = useState("");
    const [reverseDate, setReverseDate] = useState("");
    const reverseChoices = useMemo(() => mapping.filter((row) => row.hh_code === item.actual_hh_target_code && row.can_import), [mapping, item.actual_hh_target_code]);
    const reverseSelected = reverseChoices.find((row) => row.hr_status === reverseStatus) || reverseChoices[0];
    const selectedDefinition = statuses.find((status) => status.code === selectedStatus);
    const changed = selectedStatus !== item.platform_status;
    const outboundPreview = mapping.find((row) => row.hr_status === selectedStatus);

    const adoptHh = async () => {
        if (!reverseSelected) return;
        if (reverseSelected.requires_date && !reverseDate) {
            showAlert("Для выбранного статуса HR укажите дату", "warning");
            return;
        }
        setSaving(true);
        try {
            await importCandidateStatusFromHh(item.candidate_id, {
                relation_id: item.relation_id,
                expected_platform_status: item.platform_status,
                expected_hh_state_code: item.actual_hh_status_code || null,
                status: reverseSelected.hr_status,
                status_date: reverseSelected.requires_date ? reverseDate : null,
            });
            showAlert("Статус HH.ru применён к кандидату на HR-платформе", "success");
            onImported();
        } catch (error) {
            showAlert(error?.message || "Не удалось перенести статус с HH.ru", "error");
        } finally {
            setSaving(false);
        }
    };

    const submit = async () => {
        if (selectedDefinition?.requiresDate && !statusDate) {
            showAlert("Для выбранного статуса укажите дату", "warning");
            return;
        }
        setSaving(true);
        try {
            const result = await applyCandidateHhStatus(item.candidate_id, {
                relation_id: item.relation_id,
                expected_platform_status: item.platform_status,
                status: selectedStatus,
                status_date: selectedDefinition?.requiresDate ? statusDate : null,
            });
            showAlert(
                result?.platform_changed
                    ? "Статус изменён на платформе и поставлен в очередь отправки на HH.ru"
                    : "Текущий статус платформы поставлен в очередь отправки на HH.ru",
                "success"
            );
            onQueued(
                item.candidate_id,
                result?.platform_status || selectedStatus,
                result?.platform_status_label || selectedDefinition?.name,
                result?.expected_hh_status
            );
        } catch (error) {
            showAlert(error?.message || "Не удалось изменить и синхронизировать статус", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <article className="rounded-2xl border border-rose-200 bg-white p-5 shadow-sm" data-testid={`hh-sync-item-${item.candidate_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Link to={`/candidates?candidate_id=${item.candidate_id}`} className="text-lg font-bold text-slate-900 hover:text-[#9b7c13] hover:underline">
                        {item.candidate_name}
                    </Link>
                    <p className="mt-1 text-sm text-slate-500">{item.vacancy_name}</p>
                </div>
                {item.delivery_status === "Dead" ? (
                    <div className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                        Нужна ручная синхронизация
                    </div>
                ) : null}
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Должно быть по HR-платформе</p>
                    <p className="mt-1 font-bold text-slate-900">{item.platform_status_label}</p>
                    <p className="mt-1 text-sm text-slate-600">Статус HH: {item.expected_hh_status}</p>
                </div>
                <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Сейчас на HH.ru</p>
                    <p className="mt-1 font-bold text-slate-900">{item.actual_hh_status || "Статус не получен"}</p>
                    <p className="mt-1 text-sm text-slate-600">Проверено: {displayDate(item.checked_at)}</p>
                </div>
            </div>

            <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                <span className="font-semibold">Что не синхронизировано: </span>{item.issue}
            </div>

            <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
                <div className="min-w-[260px] flex-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <p>Статус HR для отправки на HH.ru</p>
                    <PlatformStatusPicker
                        statuses={statuses}
                        value={selectedStatus}
                        currentStatus={item.platform_status}
                        onChange={(statusCode) => {
                            setSelectedStatus(statusCode);
                            setStatusDate("");
                        }}
                        testId={`hh-sync-status-${item.candidate_id}`}
                    />
                </div>
                {selectedDefinition?.requiresDate ? (
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Дата статуса
                        <input type="date" value={statusDate} onChange={(event) => setStatusDate(event.target.value)} className="mt-2 block rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800" />
                    </label>
                ) : null}
                <button
                    type="button"
                    onClick={submit}
                    disabled={saving || !item.can_retry}
                    className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {saving ? "Отправляем…" : changed ? "Сменить статус и синхронизировать" : "Проставить текущий статус на HH.ru"}
                </button>
            </div>
            <p className="mt-3 text-sm text-slate-700" data-testid={`hh-sync-outbound-preview-${item.candidate_id}`}>
                Результат в HH.ru: <b>{outboundPreview?.hh_label || (changed ? "Соответствие не определено" : item.expected_hh_status)}</b>
                {changed ? " после смены статуса HR" : " по текущему статусу HR"}.
            </p>
            <div className="mt-5 rounded-xl border border-sky-200 bg-sky-50 p-4">
                <h3 className="text-sm font-bold text-sky-950">Принять статус с HH.ru на платформу</h3>
                {reverseSelected ? <>
                    <p className="mt-1 text-sm text-sky-900">Сейчас в HH.ru: <b>{item.actual_hh_status}</b>. Выберите соответствующий статус HR — автоматического выбора между разными этапами нет.</p>
                    <div className="mt-3 flex flex-wrap items-end gap-3">
                        <label className="min-w-[260px] flex-1 text-sm font-medium text-slate-800">Статус HR после переноса
                            <select value={reverseSelected.hr_status} onChange={(event) => { setReverseStatus(event.target.value); setReverseDate(""); }} data-testid={`hh-sync-reverse-status-${item.candidate_id}`} className="mt-1 block w-full rounded-xl border border-sky-200 bg-white px-3 py-2.5">
                                {reverseChoices.map((row) => <option key={row.hr_status} value={row.hr_status}>{row.hr_label}</option>)}
                            </select>
                        </label>
                        {reverseSelected.requires_date ? <label className="text-sm font-medium text-slate-800">Дата статуса HR
                            <input type="date" value={reverseDate} onChange={(event) => setReverseDate(event.target.value)} className="mt-1 block rounded-xl border border-sky-200 bg-white px-3 py-2.5" />
                        </label> : null}
                        <button type="button" onClick={adoptHh} disabled={saving || !item.can_retry} className="rounded-xl bg-sky-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50">Проставить статус HH на HR</button>
                    </div>
                    <p className="mt-3 text-sm text-sky-950" data-testid={`hh-sync-reverse-preview-${item.candidate_id}`}>Результат на HR-платформе: <b>{reverseSelected.hr_label}{reverseSelected.requires_date && reverseDate ? ` · ${displayStatusDate(reverseDate)}` : ""}</b>.</p>
                </> : <p className="mt-2 text-sm text-sky-900">Для этого статуса HH.ru нет безопасного прямого переноса. Уточните этап вручную: оформление в штат и перевод требуют отдельных действий.</p>}
            </div>
            {!item.can_retry ? <p className="mt-2 text-sm text-rose-700">Нужны ссылка на резюме и привязанная вакансия HH.ru.</p> : null}
        </article>
    );
}

export default function CandidateSyncIssuesPage() {
    const { showAlert } = useAlertContext();
    const [items, setItems] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [mapping, setMapping] = useState([]);
    const [mappingError, setMappingError] = useState(false);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [refreshNonce, setRefreshNonce] = useState(0);

    const load = useCallback(async (signal) => {
        setLoading(true);
        setLoadError(false);
        try {
            const data = await getHhSyncMismatches({ search, status, page, perPage: 20, signal });
            setItems(data?.items || []);
            setTotal(Number(data?.total || 0));
            setTotalPages(Number(data?.total_pages || 0));
        } catch (error) {
            if (error?.name !== "AbortError") {
                setLoadError(true);
                showAlert(error?.message || "Не удалось загрузить список рассинхронизации", "error");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [page, search, showAlert, status]);

    useEffect(() => {
        getCandidatesStatuses().then(setStatuses).catch(() => setStatuses([]));
        getCandidateHhStatusMapping().then((data) => { setMapping(data?.items || []); setMappingError(false); }).catch(() => { setMapping([]); setMappingError(true); });
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        const timer = window.setTimeout(() => load(controller.signal), 200);
        return () => { window.clearTimeout(timer); controller.abort(); };
    }, [load, refreshNonce]);

    const onQueued = (candidateId, selectedStatus, selectedLabel, expectedHhStatus) => {
        setItems((current) => current.map((item) => item.candidate_id === candidateId ? {
            ...item,
            platform_status: selectedStatus,
            platform_status_label: selectedLabel || item.platform_status_label,
            expected_hh_status: expectedHhStatus || item.expected_hh_status,
            delivery_status: "Pending",
            delivery_attempts: 0,
            issue: "Статус поставлен в очередь. Фоновая задача проверит результат на HH.ru.",
        } : item));
    };

    return (
        <MainLayout>
            <div className="space-y-6">
                <header className="rounded-3xl bg-slate-900 p-6 text-white shadow-lg">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Контроль HH.ru</p>
                            <h1 className="mt-2 text-3xl font-bold">Несинхронизированные кандидаты</h1>
                            <p className="mt-2 max-w-3xl text-sm text-slate-300">Здесь можно отправить статус HR на HH.ru либо по вашему решению принять статус HH.ru на платформу. Оба результата показаны до применения.</p>
                        </div>
                        <div className="rounded-2xl bg-white/10 px-5 py-3 text-center"><b className="block text-2xl">{total}</b><span className="text-xs text-slate-300">требуют проверки</span></div>
                    </div>
                </header>

                {mappingError ? <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Не удалось загрузить справочник соответствий. Обратный перенос временно недоступен; обновите страницу.</div> : null}

                <details className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="hh-status-mapping-guide">
                    <summary className="cursor-pointer text-base font-bold text-slate-900">Справочник статусов HR ↔ HH.ru <span className="ml-2 text-sm font-normal text-slate-500">Все соответствия и правила переноса</span></summary>
                    <p className="mt-3 text-sm text-slate-600">Несколько этапов HR могут соответствовать одному статусу HH.ru. При переносе обратно выберите нужный этап и дату, если она требуется. «В штате» и «Перевод» оформляются только через специальные действия платформы.</p>
                    <div className="mt-4 max-h-[480px] overflow-auto rounded-xl border border-slate-200">
                        <table className="w-full min-w-[600px] border-collapse text-left text-sm">
                            <thead className="sticky top-0 bg-slate-100 text-slate-700"><tr><th className="px-4 py-3">Статус HR-платформы</th><th className="px-4 py-3">Соответствующий статус HH.ru</th><th className="px-4 py-3">Перенос с HH</th></tr></thead>
                            <tbody>{mapping.map((row) => <tr key={row.hr_status} className="border-t border-slate-100"><td className="px-4 py-2.5 font-medium text-slate-900">{row.hr_label}{row.requires_date ? " · нужна дата" : ""}</td><td className="px-4 py-2.5 text-slate-700">{row.hh_label}</td><td className="px-4 py-2.5 text-slate-600">{row.can_import ? "Можно выбрать" : "Отдельное действие"}</td></tr>)}</tbody>
                        </table>
                    </div>
                </details>

                <section className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_280px_auto]">
                    <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Поиск по ФИО или вакансии" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:border-[#cda834] focus:outline-none focus:ring-2 focus:ring-[#cda834]/30" />
                    <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm">
                        <option value="">Все статусы платформы</option>
                        {statuses.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
                    </select>
                    <button type="button" onClick={() => setRefreshNonce((value) => value + 1)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50">Обновить</button>
                </section>

                {loading ? <div className="py-16 text-center text-slate-500">Проверяем статусы…</div> : loadError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center text-rose-800">Не удалось получить список кандидатов. Проверьте соединение и нажмите «Обновить».</div>
                ) : items.length ? (
                    <div className="space-y-4">{items.map((item) => <MismatchCard key={item.relation_id} item={item} statuses={statuses} mapping={mapping} onQueued={onQueued} onImported={() => setRefreshNonce((value) => value + 1)} />)}</div>
                ) : (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center"><h2 className="text-xl font-bold text-emerald-900">Все кандидаты синхронизированы</h2><p className="mt-2 text-sm text-emerald-700">Расхождений между HR-платформой и HH.ru не найдено.</p></div>
                )}

                {totalPages > 1 ? <nav className="flex items-center justify-center gap-3"><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border bg-white px-3 py-2 disabled:opacity-40">Назад</button><span className="text-sm text-slate-600">Страница {page} из {totalPages}</span><button disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border bg-white px-3 py-2 disabled:opacity-40">Вперёд</button></nav> : null}
            </div>
        </MainLayout>
    );
}
